import { FC, ReactElement } from 'react';

import localLogo from '../../../assets/icons/header/platform/logo.svg';
import { usePlatformData } from '../../../features/home/hooks/usePlatformData';
import DateRange from '../../../shared/components/DateRange';

interface EventData {
    logoUrl?: string | null;
    startDate?: string | Date;
    endDate?: string | Date;
    venue?: string;
    location?: string;
}

interface PlatformProps {
    eventData?: EventData | null;
    loading?: boolean;
}

const Platform: FC<PlatformProps> = ({ eventData: propEventData, loading: propLoading }): ReactElement => {
    // Use prop data if provided (e.g. for Storybook), otherwise fetch internally
    const { eventData: fetchedData, loading: fetchedLoading } = usePlatformData();
    
    const eventData = propEventData || fetchedData;
    const loading = propLoading !== undefined ? propLoading : fetchedLoading;
    const displayLogo: string | undefined = localLogo || eventData?.logoUrl || undefined;

    // RENDER: Always render the container structure immediately
    return (
        <div className="w-full h-[98px] min-[576px]:h-[122px] flex items-center bg-[#FFFFFF] px-[20px] py-[12px] min-[576px]:px-[24px] min-[576px]:py-[24px] border-b border-[#FFFFFF1A]">
            <div className="w-full max-w-[350px] min-[576px]:max-w-[1240px] mx-auto h-full flex items-center justify-between">

                {/* --- LEFT SIDE: LOGO --- */}
                <div className="flex items-center">
                    {displayLogo ? (
                        // REAL: Image (Local or Remote)
                        <img
                            src={displayLogo}
                            alt="Informa Brand"
                            className="h-[46px] object-contain"
                        />
                    ) : (
                        // FALLBACK: If no logo found
                        <div className="w-[122px] h-[46px] flex items-center justify-center bg-gray-50 text-xs text-gray-400">
                            No Logo
                        </div>
                    )}
                </div>

                {/* --- RIGHT SIDE: EVENT INFO --- */}
                 <div className="flex flex-col items-end justify-center text-right space-y-[4px]">
                    {loading ? (
                        // SKELETON: Text lines
                        <>
                            <div className="h-4 w-32 bg-gray-200 animate-pulse rounded" />
                            <div className="h-3 w-24 bg-gray-200 animate-pulse rounded" />
                            <div className="h-3 w-20 bg-gray-200 animate-pulse rounded" />
                        </>
                    ) : eventData ? (
                        // REAL: Text content
                        <>
                            <div className="text-[#182230] text-sm font-semibold tracking-normal h-[24px] flex items-center leading-tight">
                                <DateRange
                                    startDate={eventData.startDate}
                                    endDate={eventData.endDate}
                                    className=""
                                />
                            </div>
                            <div className="flex flex-col items-end justify-end">
                                <div className="text-[#667085] text-sm font-normal tracking-normal flex items-center leading-tight mb-1">
                                    {eventData.venue}
                                </div>
                                <div className="text-[#667085] text-sm font-normal tracking-normal flex items-center leading-tight">
                                    {eventData.location}
                                </div>
                            </div>
                        </>
                    ) : (
                        // ERROR: If loaded but data missing
                        <div className="text-red-500 text-xs">Data unavailable</div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default Platform;
